import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  AccessTime,
  Group,
  LocalFireDepartment,
  FitnessCenter
} from '@mui/icons-material';
import { Map, Marker } from "pigeon-maps";
import rosieKeg from '../assets/Rose and Kristi.jpg';
interface OwnerInfo {
  name: string;
  role: string;
  description: string;
}

interface ContactInfo {
  icon: React.ReactNode;
  label: string;
  value: string;
  link?: string;
}

const About: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mapError, setMapError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);

  // Add timeout fallback for map loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapLoading) {
        setMapLoading(false);
        // If map is still loading after 8 seconds, show error after a 2 second delay
        setTimeout(() => setMapError(true), 2000);
      }
    }, 8000);

    return () => clearTimeout(timer);
  }, [mapLoading]);

  // Reset map state when component mounts
  useEffect(() => {
    setMapLoading(true);
    setMapError(false);
    
    // Add a small delay to ensure the map container is properly rendered
    const initTimer = setTimeout(() => {
      // Force a re-render of the map after a short delay
      setMapLoading(false);
      setTimeout(() => setMapLoading(true), 100);
    }, 500);

    return () => clearTimeout(initTimer);
  }, []);

  const owners: OwnerInfo[] = [
    {
      name: "Kristi Gaskill",
      role: "Co-Owner & Fitness Enthusiast",
      description: "Kristi has been passionate about fitness for years and loves incorporating heated fitness into her daily routine. She's the one who convinced Rose to join her for that first hot yoga class that started it all."
    },
    {
      name: "Rose Gillen",
      role: "Co-Owner & Fitness Enthusiast",
      description: "Rose was initially skeptical about handling the heat, but after that first class at Kris' Hot Yoga (now The Burn), she was hooked. Together with Kristi, they've built a welcoming fitness community."
    }
  ];

  const contactInfo: ContactInfo[] = [
    {
      icon: <LocationOn />,
      label: "Address",
      value: "2505 SW White Birch Dr., Suite A&B\nAnkeny, IA 50023"
    },
    {
      icon: <Phone />,
      label: "Phone",
      value: "515-216-3888",
      link: "tel:515-216-3888"
    },
    {
      icon: <AccessTime />,
      label: "Hours",
      value: "Check our class schedule for current hours"
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E3F2FD', position: 'relative', overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box sx={{ 
        py: 8, 
        background: `linear-gradient(135deg, #E3F2FD 0%, ${theme.palette.primary.main}15 100%)`,
        textAlign: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                About The Burn
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
              >
                We are Kristi Gaskill and Rose Gillen, owners of The Burn. We both have been fitness enthusiasts 
                for years and enjoy incorporating heated fitness into our everyday routines.
              </Typography>
              
              {/* Story Overview */}
              <Grid container spacing={3} sx={{ maxWidth: 800, mx: 'auto' }}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Group sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Friendship
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <LocalFireDepartment sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Heated Fitness
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <FitnessCenter sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Community
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: 8, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  Our Story
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3 }}>
                  Our friendship was established many years ago after Kristi convinced Rose (who was skeptical 
                  she could handle the heat) to join her for a class at Kris' Hot Yoga (now The Burn) - and 
                  the rest is history.
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  We would love it if The Burn became a part of your lifestyle - come meet us at the studio! 
                  We're passionate about creating a welcoming environment where everyone can feel the burn and 
                  discover their strength.
                </Typography>
                
                {/* Values */}
                <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                  <Chip
                    label="Welcoming"
                    color="primary"
                    variant="outlined"
                    icon={<Group />}
                  />
                  <Chip
                    label="Inclusive"
                    color="secondary"
                    variant="outlined"
                    icon={<FitnessCenter />}
                  />
                  <Chip
                    label="Community"
                    color="success"
                    variant="outlined"
                    icon={<LocalFireDepartment />}
                  />
                </Stack>
              </Box>
            </Fade>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Grow in timeout={1200}>
              <Card
                elevation={8}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
              >
                <CardMedia
                  component="img"
                  image={rosieKeg}
                  alt="Kristi and Rose - The Burn Owners"
                  sx={{
                    height: 400,
                    objectFit: 'cover',
                  }}
                />
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Kristi & Rose
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Co-Owners of The Burn
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        </Grid>
      </Container>

      {/* Meet the Owners Section */}
      <Box sx={{ py: 8, bgcolor: '#E3F2FD', position: 'relative', zIndex: 2 }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 700 }}
          >
            Meet Your Co-Owners
          </Typography>
          
          <Grid container spacing={4}>
            {owners.map((owner, index) => (
              <Grid item xs={12} md={6} key={owner.name}>
                <Grow in timeout={1000 + (index * 200)}>
                  <Card
                    elevation={4}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Avatar
                          sx={{
                            width: 120,
                            height: 120,
                            mx: 'auto',
                            mb: 2,
                            bgcolor: 'primary.main',
                            fontSize: '2rem',
                          }}
                        >
                          {owner.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                          {owner.name}
                        </Typography>
                        <Typography variant="subtitle1" color="primary.main" gutterBottom>
                          {owner.role}
                        </Typography>
                      </Box>
                      
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.7,
                          flexGrow: 1,
                          textAlign: 'center',
                        }}
                      >
                        {owner.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Location & Contact Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ mb: 6, fontWeight: 700 }}
        >
          Visit Us
        </Typography>
        
        <Grid container spacing={6}>
          {/* Map */}
          <Grid item xs={12} md={8}>
            <Grow in timeout={1000}>
              <Card
                elevation={4}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: 400,
                    height: 400,
                    width: '100%',
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.300'
                  }}>
                    {mapLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.50',
                          zIndex: 1,
                          borderRadius: '8px',
                        }}
                      >
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Loading map...
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                          This may take a few moments
                        </Typography>
                      </Box>
                    )}
                    
                    {mapError ? (
                      <Box
                        sx={{
                          height: 400,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 3,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <LocationOn sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          Visit Our Studio
                        </Typography>
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2 }}>
                          2505 SW White Birch Dr., Suite A&B<br />
                          Ankeny, IA 50023
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                          Located in the White Birch Business Park
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                          <Chip
                            label="Retry Map"
                            color="primary"
                            variant="outlined"
                            onClick={() => {
                              setMapError(false);
                              setMapLoading(true);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                          <Chip
                            label="View on Google Maps"
                            color="secondary"
                            variant="outlined"
                            onClick={() => window.open('https://maps.google.com/?q=2505+SW+White+Birch+Dr+Ankeny+IA+50023', '_blank')}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Map 
                        key={`map-${mapLoading}-${mapError}`}
                        height={400} 
                        center={[41.704400, -93.626800]} 
                        zoom={16}
                        onBoundsChanged={() => setMapLoading(false)}
                        provider={(x, y, z, dpr) => {
                          // Use a different tile provider that's more reliable
                          return `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
                        }}
                        attribution={false}
                      >
                        <Marker 
                          width={50} 
                          anchor={[41.704400, -93.626800]}
                          color={theme.palette.primary.main}
                        />
                      </Map>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
          
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {contactInfo.map((info, index) => (
                <Grow in timeout={1200 + (index * 200)} key={info.label}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            mr: 2,
                          }}
                        >
                          {info.icon}
                        </Box>
                        <Typography variant="h6" component="h4" sx={{ fontWeight: 600 }}>
                          {info.label}
                        </Typography>
                      </Box>
                      
                      <Typography
                        variant="body1"
                        component={info.link ? 'a' : 'p'}
                        href={info.link}
                        sx={{
                          color: info.link ? 'primary.main' : 'text.secondary',
                          textDecoration: info.link ? 'none' : 'inherit',
                          '&:hover': {
                            textDecoration: info.link ? 'underline' : 'inherit',
                          },
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {info.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ py: 8, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, mb: 3 }}
          >
            Ready to Join Our Community?
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: 'auto' }}
          >
            Come meet us at the studio and experience the welcoming atmosphere we've created. 
            We can't wait to help you discover your strength and become part of The Burn family.
          </Typography>
          
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={3}
            sx={{ justifyContent: 'center' }}
          >
            <Box
              component="a"
              href="/classes/schedule"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  px: 4,
                  py: 2,
                  bgcolor: 'white',
                  color: 'primary.main',
                  borderRadius: 3,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  },
                }}
              >
                View Class Schedule
              </Typography>
            </Box>
            
            <Box
              component="a"
              href="/membership"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  px: 4,
                  py: 2,
                  border: '2px solid white',
                  borderRadius: 3,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Explore Memberships
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
